/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {environment} from '../../../environments/environment';
import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {ClaimCodeBaseModel} from './contracts/claim-code-base.model';
import {PartnerBaseModel} from './contracts/partner-base.model';
import {SupplyingPackingPlantBaseModel} from './contracts/supplying-packing-plant-base.model';
import {ClaimErrorBaseModel} from './contracts/claim-error-base.model';
import {BusinessCategoryBaseModel} from './contracts/business-category-base.model';
import {Cacheable} from '../../decorators/cacheable.decorator';
import {PartnerGroupBaseModel} from './contracts/partner-group-base.model';
import {ClaimStatusBaseModel} from './contracts/claim-status-base.model';
import {DecisionDetailModel} from './contracts/decision-detail.model';

@Injectable({
    providedIn: 'root'
})
export class ReferenceDataApi {
    private referenceDataBaseUrl = `${environment.claimsBaseUrl}/api`;
    private partnerBaseUrl = `${this.referenceDataBaseUrl}/partner/base/active`;
    private supplyingPackingPlantBaseUrl = `${this.referenceDataBaseUrl}/supplyingPackingPlant/base`;
    private partnerBaseGroupByCountryUrl = `${this.referenceDataBaseUrl}/partner/base/groupBy/country`;
    private claimErrorBaseUrl = `${this.referenceDataBaseUrl}/claimError/base/active`;
    private businessCategoryUrl = `${this.referenceDataBaseUrl}/businessCategory/base/active`;
    private statusBaseUrl = `${this.referenceDataBaseUrl}/claim/status/base`;
    private toRemoveClaimCodeBaseUrl = `${this.referenceDataBaseUrl}/claim/code/base`;
    private decisionMatrixBaseUrl = `${environment.claimsBaseUrl}/api/decisionMatrix`;
    private supplyingPackingPlantFromMatrixBaseUrl = `${this.decisionMatrixBaseUrl}/supplyingPackingPlant/base/active`;
    private claimCodeBaseUrl = `${this.decisionMatrixBaseUrl}/claimCode/base/active`;
    private decisionCodeBaseUrl = `${this.decisionMatrixBaseUrl}/decision/detail/active`;

    constructor(protected http: HttpClient) {
    }

    @Cacheable()
    getActivePartners(): Observable<PartnerBaseModel[]> {
        return this.http.get<PartnerBaseModel[]>(this.partnerBaseUrl);
    }

    @Cacheable()
    getSupplyingPackingPlantsGroupOptions(): Observable<SupplyingPackingPlantBaseModel[]> {
        return this.http.get<SupplyingPackingPlantBaseModel[]>(this.supplyingPackingPlantBaseUrl);
    }

    @Cacheable()
    getActiveErrors(): Observable<ClaimErrorBaseModel[]> {
        return this.http.get<ClaimErrorBaseModel[]>(this.claimErrorBaseUrl);
    }

    @Cacheable()
    getActiveBiCategories(): Observable<BusinessCategoryBaseModel[]> {
        return this.http.get<BusinessCategoryBaseModel[]>(this.businessCategoryUrl);
    }

    @Cacheable()
    getPartnersGroupOptions(): Observable<PartnerGroupBaseModel[]> {
        return this.http.get<PartnerGroupBaseModel[]>(this.partnerBaseGroupByCountryUrl);
    }

    @Cacheable()
    getStatusOptions(): Observable<ClaimStatusBaseModel[]> {
        return this.http.get<ClaimStatusBaseModel[]>(this.statusBaseUrl);
    }

    @Cacheable()
    getClaimCodes(): Observable<ClaimCodeBaseModel[]> {
        return this.http.get<ClaimCodeBaseModel[]>(this.toRemoveClaimCodeBaseUrl);
    }

    @Cacheable()
    getActiveSupplyingPackingPlants(partnerCode: string): Observable<SupplyingPackingPlantBaseModel[]> {
        return this.http.get<SupplyingPackingPlantBaseModel[]>(`${this.supplyingPackingPlantFromMatrixBaseUrl}?partnerCode=${partnerCode}`);
    }

    @Cacheable()
    getActiveClaimCodes(partnerCode: string, plantCode: string): Observable<ClaimCodeBaseModel[]> {
        return this.http.get<ClaimCodeBaseModel[]>(`${this.claimCodeBaseUrl}?` + [
            `partnerCode=${partnerCode}`,
            `plantCode=${plantCode}`
        ].join('&'));
    }

    @Cacheable()
    getActiveDecisionCodes(partnerCode: string, plantCode: string, claimCode: string): Observable<DecisionDetailModel[]> {
       return this.http.get<DecisionDetailModel[]>(`${this.decisionCodeBaseUrl}?` + [
            `partnerCode=${partnerCode}`,
            `plantCode=${plantCode}`,
            `claimCode=${claimCode}`
        ].join('&'));
    }
}
